/**
 * OAuth 2.0 Service for Microsoft 365 Authentication
 *
 * Handles the OAuth flow for internal users authenticating with Azure AD.
 * External clients (customers) will continue using password authentication.
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

export interface OAuthAuthorizationResponse {
  authorization_url: string;
  state: string;
}

export interface OAuthCallbackResponse {
  access_token: string;
  token_type: string;
  user: {
    id: number;
    email: string;
    full_name: string;
    role: string;
    is_active: boolean;
  };
}

class OAuthService {
  /**
   * Initiate Microsoft OAuth 2.0 flow
   * Redirects user to Microsoft login page
   */
  async initiateLogin(): Promise<void> {
    try {
      // Get current URL for callback
      const redirectUri = `${window.location.origin}/auth/callback`;

      // Request authorization URL from backend
      const response = await axios.get<OAuthAuthorizationResponse>(
        `${API_URL}/auth/oauth/microsoft/authorize`,
        {
          params: { redirect_uri: redirectUri }
        }
      );

      const { authorization_url, state } = response.data;

      // Store state in sessionStorage for CSRF protection
      sessionStorage.setItem('oauth_state', state);
      sessionStorage.setItem('oauth_redirect_uri', redirectUri);

      // Redirect to Microsoft login
      window.location.href = authorization_url;
    } catch (error) {
      console.error('Failed to initiate OAuth login:', error);
      throw new Error('Failed to start Microsoft login. Please try again.');
    }
  }

  /**
   * Handle OAuth callback after user authenticates with Microsoft
   * Exchanges authorization code for access token
   */
  async handleCallback(code: string, state: string): Promise<OAuthCallbackResponse> {
    try {
      // Verify state matches (CSRF protection)
      const savedState = sessionStorage.getItem('oauth_state');
      if (state !== savedState) {
        throw new Error('Invalid state parameter. Possible CSRF attack.');
      }

      const redirectUri = sessionStorage.getItem('oauth_redirect_uri');
      if (!redirectUri) {
        throw new Error('Redirect URI not found in session');
      }

      // Exchange code for token
      const response = await axios.post<OAuthCallbackResponse>(
        `${API_URL}/auth/oauth/microsoft/callback`,
        null,
        {
          params: {
            code,
            state,
            redirect_uri: redirectUri
          }
        }
      );

      // Clean up session storage
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('oauth_redirect_uri');

      return response.data;
    } catch (error: any) {
      console.error('OAuth callback failed:', error);

      // Clean up session storage on error
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('oauth_redirect_uri');

      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw new Error('Authentication failed. Please try again.');
    }
  }
}

export const oauthService = new OAuthService();
